import { useNavigate } from 'react-router-dom';
import { Briefcase, ListChecks, Wrench } from 'lucide-react';

import { Alert, Badge, Card } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useActivitiesToday, type ActivityCard } from '@/lib/home-today-store';

/**
 * AtividadesHojeWidget — atividades do dia do user (R8 Bloco E).
 *
 * Consome `useActivitiesToday()`. 3 seções colapsadas verticalmente:
 *   - Atrasadas (vermelho) — prazo passou
 *   - Hoje (warning) — prazo cai em hoje
 *   - Próximas (neutral) — prazo em até 3 dias
 *
 * Click numa atividade navega pra URL do card (gerada no backend com
 * ?openDeal= ou ?openOs= query params).
 *
 * Empty state: "Você está em dia" quando todos os buckets vazios.
 */

export function AtividadesHojeWidget() {
  const { data, isLoading, isError, error } = useActivitiesToday();
  const navigate = useNavigate();

  function handleClick(card: ActivityCard) {
    navigate(card.url);
  }

  const total = data
    ? data.count.overdue + data.count.today + data.count.upcoming
    : 0;

  return (
    <Card>
      <div className="p-5">
        <header className="mb-3">
          <h2 className="font-semibold text-lg text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-ink-3 dark:text-ink-dark-3" aria-hidden />
            Atividades do dia
          </h2>
        </header>

        {isLoading ? (
          <Skeleton />
        ) : isError ? (
          <Alert variant="danger" rail>
            Não consegui carregar suas atividades.
            <br />
            <span className="text-xs opacity-70">
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </span>
          </Alert>
        ) : data && total === 0 ? (
          <p
            className="text-sm text-ink-3 dark:text-ink-dark-3 text-center py-8"
            data-testid="atividades-empty"
          >
            {/* Auditoria visual (13/10/2026): sem travessão no texto da tela. */}
            Você está em dia: nenhuma atividade pendente.
          </p>
        ) : data ? (
          <div className="space-y-4" data-testid="atividades-list">
            <Section
              title="Atrasadas"
              count={data.count.overdue}
              variant="danger"
              cards={data.overdue}
              onClickCard={handleClick}
              testid="atividades-overdue"
            />
            <Section
              title="Hoje"
              count={data.count.today}
              variant="warning"
              cards={data.today}
              onClickCard={handleClick}
              testid="atividades-today"
            />
            <Section
              title="Próximas"
              count={data.count.upcoming}
              variant="neutral"
              cards={data.upcoming}
              onClickCard={handleClick}
              testid="atividades-upcoming"
            />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3 animate-pulse" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 bg-paper-warm dark:bg-paper-dark-warm rounded" />
      ))}
    </div>
  );
}

interface SectionProps {
  title: string;
  count: number;
  variant: 'danger' | 'warning' | 'neutral';
  cards: ActivityCard[];
  onClickCard: (card: ActivityCard) => void;
  testid: string;
}

function Section({ title, count, variant, cards, onClickCard, testid }: SectionProps) {
  if (count === 0) return null;
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Badge variant={variant} size="sm">
          {title} · {count}
        </Badge>
      </div>
      <ul className="space-y-1">
        {cards.slice(0, 5).map((c) => (
          <CardRow key={c.id} card={c} onClick={() => onClickCard(c)} />
        ))}
        {cards.length > 5 && (
          <li className="text-xs text-ink-3 dark:text-ink-dark-3 pl-2">
            + {cards.length - 5} {cards.length - 5 === 1 ? 'outra' : 'outras'}…
          </li>
        )}
      </ul>
    </section>
  );
}

function CardRow({ card, onClick }: { card: ActivityCard; onClick: () => void }) {
  return (
    <li
      data-testid={`atividade-card-${card.id}`}
      className={cn(
        'flex items-baseline gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
        'hover:bg-paper-warm dark:hover:bg-paper-dark-warm'
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* FASE-C: emoji 💼/🔧 virou ícone Lucide com rótulo acessível. */}
      <span
        className="text-ink-3 dark:text-ink-dark-3 flex-shrink-0"
        title={card.type === 'deal' ? 'Proposta' : 'Ordem de serviço'}
      >
        {card.type === 'deal' ? (
          <Briefcase className="w-3.5 h-3.5" aria-label="Proposta" />
        ) : (
          <Wrench className="w-3.5 h-3.5" aria-label="Ordem de serviço" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-ink-1 dark:text-ink-dark-1 truncate">
          {card.clienteNome}
        </p>
        <p className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">
          {card.title} · {formatPrazo(card.prazoIso)}
        </p>
      </div>
    </li>
  );
}

function formatPrazo(iso: string | null): string {
  if (!iso) return 'sem prazo';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));
    if (diffDays < -1) return `há ${Math.abs(diffDays)} dias`;
    if (diffDays === -1) return 'ontem';
    if (diffDays === 0) return 'hoje';
    if (diffDays === 1) return 'amanhã';
    return `em ${diffDays} dias`;
  } catch {
    return '—';
  }
}
